import React, { useEffect, useState } from 'react';
import { ordersAPI, devicesAPI } from '../services/api';
import { Plus, RefreshCw, X, LogIn, LogOut } from 'lucide-react';

const STATUSES = ['', 'PENDING', 'CONFIRMED', 'CHECK_IN', 'IN_PROGRESS', 'CHECK_OUT', 'CANCELLED'];
const STATUS_LABELS = { '': 'All', PENDING: 'Pending', CONFIRMED: 'Confirmed', CHECK_IN: 'Checked In', IN_PROGRESS: 'In Progress', CHECK_OUT: 'Checked Out', CANCELLED: 'Cancelled' };
const STATUS_COLORS = {
  PENDING: { bg: 'rgba(77,144,254,0.1)', color: '#4d90fe' },
  CONFIRMED: { bg: 'rgba(139,198,62,0.1)', color: '#8BC63E' },
  CHECK_IN: { bg: 'rgba(255,176,32,0.1)', color: '#ffb020' },
  IN_PROGRESS: { bg: 'rgba(167,139,250,0.1)', color: '#a78bfa' },
  CHECK_OUT: { bg: 'rgba(255,68,102,0.1)', color: '#ff4466' },
  CANCELLED: { bg: '#1e2029', color: '#565870' },
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ device_number: '', box_size: 'S' });

  const load = async () => {
    setLoading(true);
    try {
      const [o, d] = await Promise.all([ordersAPI.list(filter || undefined), devicesAPI.list()]);
      setOrders(o.data);
      setDevices(d.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const createOrder = async () => {
    if (!form.device_number) return alert('Select a device');
    try {
      await ordersAPI.create(form);
      setShowCreate(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const checkin = async (id) => {
    try { await ordersAPI.checkin(id); load(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const checkout = async (id) => {
    try { await ordersAPI.checkout(id); load(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this order?')) return;
    try { await ordersAPI.cancel(id); load(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Orders</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.outlineBtn} onClick={load}><RefreshCw size={14} /> Refresh</button>
          <button style={s.primaryBtn} onClick={() => setShowCreate(true)}><Plus size={14} /> Create Order</button>
        </div>
      </div>

      {/* Status Tabs */}
      <div style={s.tabs}>
        {STATUSES.map((st) => (
          <div key={st} style={{ ...s.tab, ...(filter === st ? s.tabActive : {}) }} onClick={() => setFilter(st)}>
            {STATUS_LABELS[st]}
          </div>
        ))}
      </div>

      <div style={s.panel}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Order No</th>
              <th style={s.th}>Size</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Created</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', padding: 40, color: '#565870' }}>Loading...</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', padding: 40, color: '#565870' }}>No orders found</td></tr>
            ) : orders.map((o) => {
              const sc = STATUS_COLORS[o.status] || STATUS_COLORS.CANCELLED;
              return (
                <tr key={o.id}>
                  <td style={s.td}><span style={s.mono}>{o.order_no}</span></td>
                  <td style={s.td}>{o.box_size}</td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, background: sc.bg, color: sc.color }}>{o.status.replace(/_/g, ' ')}</span>
                  </td>
                  <td style={s.td}>{new Date(o.created_at).toLocaleString()}</td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['CONFIRMED', 'PENDING'].includes(o.status) && (
                        <button style={s.smBtn} onClick={() => checkin(o.id)}><LogIn size={12} /> Check In</button>
                      )}
                      {['CHECK_IN', 'IN_PROGRESS'].includes(o.status) && (
                        <button style={s.smBtn} onClick={() => checkout(o.id)}><LogOut size={12} /> Check Out</button>
                      )}
                      {!['CHECK_OUT', 'CANCELLED'].includes(o.status) && (
                        <button style={{ ...s.smBtn, borderColor: 'rgba(255,68,102,0.3)', color: '#ff4466' }} onClick={() => cancel(o.id)}>
                          <X size={12} /> Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Order Modal */}
      {showCreate && (
        <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && setShowCreate(false)}>
          <div style={s.modal}>
            <div style={s.modalHead}>
              <h3>Create Order</h3>
              <X size={18} style={{ cursor: 'pointer', color: '#8789a0' }} onClick={() => setShowCreate(false)} />
            </div>
            <div style={s.modalBody}>
              <label style={s.label}>Device</label>
              <select style={s.input} value={form.device_number} onChange={(e) => setForm({ ...form, device_number: e.target.value })}>
                <option value="">Select device...</option>
                {devices.map(d => <option key={d.id} value={d.device_number}>{d.device_number} — {d.name}</option>)}
              </select>
              <label style={s.label}>Box Size</label>
              <select style={s.input} value={form.box_size} onChange={(e) => setForm({ ...form, box_size: e.target.value })}>
                <option value="S">Small</option>
                <option value="M">Medium</option>
                <option value="L">Large</option>
              </select>
            </div>
            <div style={s.modalFoot}>
              <button style={s.outlineBtn} onClick={() => setShowCreate(false)}>Cancel</button>
              <button style={s.primaryBtn} onClick={createOrder}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e4e5ed' },
  tabs: { display: 'flex', gap: 2, marginBottom: 0, borderBottom: '1px solid #252733', background: '#0f1117', borderRadius: '12px 12px 0 0', padding: '0 16px' },
  tab: { padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#8789a0', cursor: 'pointer', borderBottom: '2px solid transparent', transition: 'all 0.15s' },
  tabActive: { color: '#8BC63E', borderBottomColor: '#8BC63E' },
  panel: { background: '#0f1117', border: '1px solid #252733', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: '#565870', background: '#0a0b0e', borderBottom: '1px solid #252733' },
  td: { padding: '12px 16px', fontSize: 13, borderBottom: '1px solid #1a1b25', color: '#e4e5ed' },
  mono: { fontFamily: "'Space Mono', monospace", fontSize: 12 },
  badge: { display: 'inline-flex', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  smBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'transparent', border: '1px solid #252733', borderRadius: 6, color: '#8789a0', fontSize: 11, fontWeight: 500, cursor: 'pointer' },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#8BC63E', border: 'none', borderRadius: 8, color: '#07080c', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  outlineBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'transparent', border: '1px solid #252733', borderRadius: 8, color: '#8789a0', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, width: 440, color: '#e4e5ed' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #252733', fontSize: 16, fontWeight: 600 },
  modalBody: { padding: 20 },
  modalFoot: { padding: '14px 20px', borderTop: '1px solid #252733', display: 'flex', justifyContent: 'flex-end', gap: 8 },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, marginTop: 12 },
  input: { width: '100%', background: '#161821', border: '1px solid #252733', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#e4e5ed', outline: 'none', boxSizing: 'border-box' },
};

